import { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Search, ScanBarcode, UserRound, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AttributionDialog } from "@/components/scans/AttributionDialog";
import { FicheScanDialog } from "@/components/scans/FicheScanDialog";
import { api, errMsg } from "@/lib/api";
import { StatutBadge, fmtDateHeure, nomChauffeur, useScansData } from "@/components/scans/common";
import { scansSubnav } from "@/lib/subnavs";
import { useAuth } from "@/context/AuthContext";
import { peut } from "@/lib/permissions";

const FILTRES = [
  ["tous", "Toutes"], ["disponible", "Disponibles"], ["preattribuee", "Pré-attribuées"],
  ["attribuee", "Attribuées"], ["attente_constatation", "En attente de constatation"],
];

export default function RemiseScanPage() {
  const { user } = useAuth();
  const peutAttribuer = peut(user, "scan_attribuer");
  const peutAuto = peut(user, "scan_auto");
  const { scans, tournees, chauffeurs, categories, loading, reloadScans } = useScansData();
  const [dialog, setDialog] = useState(null); // { scan | null }
  const [q, setQ] = useState("");
  const [filtre, setFiltre] = useState("tous");
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoResult, setAutoResult] = useState(null);

  const actives = scans.filter((s) => s.actif !== false);
  const counts = useMemo(() => {
    const c = {};
    actives.forEach((s) => { c[s.statut] = (c[s.statut] || 0) + 1; });
    return c;
  }, [actives]);

  const shown = actives.filter((s) => {
    if (filtre !== "tous" && s.statut !== filtre) return false;
    const t = q.trim().toLowerCase();
    if (!t) return true;
    const ch = s.attribution?.chauffeur;
    return [s.numero, s.tournee_defaut, s.attribution?.tournee, ch?.nom, ch?.prenom].some((v) => (v || "").toLowerCase().includes(t));
  });

  // Scans dont la pré-attribution est activée (Config scan) et prêtes.
  const autoConfig = actives.filter((s) => s.preattribution_auto?.actif);
  const autoPretes = autoConfig.filter((s) => s.statut === "disponible");

  const autoAttribuer = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.post("/scans/auto-attribution");
      setAutoResult(data);
      reloadScans();
    } catch (e) { toast.error(errMsg(e)); } finally { setAutoBusy(false); }
  };

  const [fiche, setFiche] = useState(null);   // scan remise : historique + code-barres
  const ouvrir = (s) => {
    if ((s.statut === "disponible" || s.statut === "preattribuee") && peutAttribuer) setDialog({ scan: s });
    else setFiche(s);
  };

  return (
    <AppShell title="Remise de scan" subnav={scansSubnav()}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Remise de scan</h1>
          <p className="text-sm text-muted-foreground">
            {counts.disponible || 0} disponible(s) · {counts.preattribuee || 0} pré-attribuée(s) · {counts.attribuee || 0} chez les chauffeurs
            {counts.attente_constatation ? ` · ${counts.attente_constatation} en attente de constatation` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {peutAuto && <Button
            variant="outline"
            onClick={() => (autoConfig.length ? autoAttribuer() : toast.info("Aucune scan n'a de pré-attribution activée : configurez-la dans « Config scan » (bouton « Pré-attribution »)."))}
            disabled={autoBusy}
            title="Pré-attribue les scans configurées dans « Config scan »"
            data-testid="btn-auto-attribution"
          >
            {autoBusy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Auto-attribution
            {autoConfig.length > 0 && <span className="ml-1 text-[11px] px-1.5 rounded-full bg-primary/15 text-primary font-semibold">{autoPretes.length}/{autoConfig.length}</span>}
          </Button>}
          {peutAttribuer && <Button onClick={() => setDialog({ scan: null })} data-testid="btn-attribuer-scan">
            <ScanBarcode size={15} /> Attribuer scan
          </Button>}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-2 mb-4">
        <div className="relative lg:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° de scan, tournée, chauffeur…" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTRES.map(([k, l]) => (
            <button key={k} onClick={() => setFiltre(k)} className={`text-xs px-3 h-9 rounded-md border ${filtre === k ? "border-primary bg-primary/10 text-primary font-semibold" : "border-border hover:bg-accent"}`}>
              {l}{k !== "tous" && counts[k] ? ` (${counts[k]})` : ""}
            </button>
          ))}
        </div>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : actives.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <ScanBarcode className="mx-auto mb-2 opacity-60" />
          Aucune scan au dépôt. Créez-les dans l'onglet « Config scan ».
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="remise-list">
          {shown.map((s) => {
            const a = s.attribution;
            const libre = s.statut === "disponible" || s.statut === "preattribuee";
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => ouvrir(s)}
                className={`text-left rounded-xl border bg-card p-4 transition-colors ${libre ? "border-border hover:border-primary/60" : "border-border/60 hover:border-primary/40"}`}
                data-testid={`scan-card-${s.numero}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Scan</p>
                    <p className="font-mono text-2xl font-bold leading-none">{s.numero}</p>
                  </div>
                  <StatutBadge statut={s.statut} label={s.statut_label} />
                </div>
                <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <p>Tournée par défaut : <span className="font-mono text-foreground">{s.tournee_defaut || "—"}</span>{s.sst ? ` · ${s.sst}` : ""}</p>
                  {a?.chauffeur && (
                    <p className="flex items-center gap-1.5 text-foreground">
                      <UserRound size={12} /> {nomChauffeur(a.chauffeur)}{a.tournee ? <span className="text-muted-foreground"> · T. {a.tournee}</span> : null}
                    </p>
                  )}
                  {s.statut === "disponible" && s.preattribution_auto?.actif && (
                    <p className="flex items-center gap-1.5"><Wand2 size={12} /> Auto : T. {s.preattribution_auto.tournee} · {nomChauffeur(s.preattribution_auto.chauffeur)}</p>
                  )}
                  {(a?.appareil?.numero_serie || s.appareil?.numero_serie) && (
                    <p className="font-mono text-foreground" data-testid={`card-materiel-${s.numero}`}>n° {a?.appareil?.numero_serie || s.appareil.numero_serie}</p>
                  )}
                  {!a?.appareil && !s.appareil && s.appareils_actifs?.length > 1 && <p>{s.appareils_actifs.length} appareils</p>}
                  {s.statut === "attribuee" && a?.signe_le && <p>Remise le {fmtDateHeure(a.signe_le)}</p>}
                  {s.defauts?.length > 0 && (
                    <p className="flex items-center gap-1 text-red-600 dark:text-red-400"><AlertTriangle size={12} /> {s.defauts.length} élément(s) défectueux</p>
                  )}
                </div>
              </button>
            );
          })}
          {shown.length === 0 && <p className="text-sm text-muted-foreground">Aucune scan ne correspond.</p>}
        </div>
      )}

      <Dialog open={!!autoResult} onOpenChange={(o) => !o && setAutoResult(null)}>
        <DialogContent className="sm:max-w-md" data-testid="auto-result">
          <DialogHeader>
            <DialogTitle>Auto-attribution</DialogTitle>
            <DialogDescription>
              {autoResult?.preattribuees.length || 0} scan(s) pré-attribuée(s). Il n'y a plus qu'à valider la remise à l'arrivée du chauffeur.
            </DialogDescription>
          </DialogHeader>
          {autoResult?.preattribuees.length > 0 && (
            <ul className="space-y-1.5 text-sm">
              {autoResult.preattribuees.map((r) => (
                <li key={r.numero} className="flex items-center gap-2">
                  <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                  <span className="font-mono font-bold">{r.numero}</span> → {r.chauffeur} <span className="text-muted-foreground">· T. {r.tournee}</span>
                </li>
              ))}
            </ul>
          )}
          {autoResult?.ignorees.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Non pré-attribuées</p>
              <ul className="space-y-1 text-sm">
                {autoResult.ignorees.map((r) => (
                  <li key={r.numero} className="flex items-center gap-2">
                    <AlertTriangle size={13} className="text-amber-500 shrink-0" />
                    <span className="font-mono font-bold">{r.numero}</span> <span className="text-muted-foreground">— {r.raison}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter><Button onClick={() => setAutoResult(null)}>OK</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <FicheScanDialog scan={fiche} onClose={() => setFiche(null)} />
      <AttributionDialog
        open={!!dialog}
        initialScan={dialog?.scan || null}
        scans={scans}
        tournees={tournees}
        chauffeurs={chauffeurs}
        categories={categories}
        onClose={() => setDialog(null)}
        onDone={() => { setDialog(null); reloadScans(); }}
      />
    </AppShell>
  );
}
